import { useEffect, useMemo, useState } from 'react';
import {
  Alert, Box, Button, Checkbox, CircularProgress, FormControlLabel,
  MenuItem, Paper, Stack, TextField, Typography, alpha,
} from '@mui/material';
import PersonOutlineRoundedIcon from '@mui/icons-material/PersonOutlineRounded';
import WorkOutlineRoundedIcon from '@mui/icons-material/WorkOutlineRounded';
import HubRoundedIcon from '@mui/icons-material/HubRounded';
import SaveRoundedIcon from '@mui/icons-material/SaveRounded';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import { getPVProfile, updatePVProfile } from '../services/projectVitalProfileService';
import {
  getPVProfessionalSettings,
  updatePVProfessionalSettings,
} from '../services/projectVitalProfessionalControlService';
import type { PVProfileField, PVProfileWrite } from '../types/projectVitalProfile';
import type { PVProfessionalSettings, PVProfessionalSettingsWrite } from '../types/projectVitalProfessionalControl';

const CYAN = '#4DD0E1';
type Tab = 'personal' | 'professional' | 'integrations';

const EMPTY_PROFILE: PVProfileWrite = {
  first_name: null,
  last_name: null,
  tax_residence_country: null,
  tax_residence_since: null,
  tax_residence_since_month: null,
  selarl_is_manager: null,
  legal_form: null,
  clinics_count: null,
  convention_scope: null,
  urssaf_registered: null,
  carcdsf_registered: null,
  acre_status: null,
  contribution_relief_status: null,
  cross_border_activity_2026: null,
  confirmed_fields: [],
};

type ProfessionalDraft = Pick<
  PVProfessionalSettingsWrite,
  'activity_status' | 'activity_name' | 'activity_start_date'
  | 'activity_year_number' | 'vat_treatment' | 'france_convention_status'
>;

const EMPTY_PROFESSIONAL: ProfessionalDraft = {
  activity_status: 'pre_activity',
  activity_name: null,
  activity_start_date: null,
  activity_year_number: 1,
  vat_treatment: 'exempt_healthcare',
  france_convention_status: 'unknown',
};

const PERSONAL_FIELDS: PVProfileField[] = [
  'first_name', 'last_name', 'tax_residence_country', 'tax_residence_since',
  'household_status', 'dependent_children',
];
const PROFESSIONAL_FIELDS: PVProfileField[] = [
  'activity_name', 'activity_start_date', 'activity_year_number',
  'vat_treatment', 'france_convention_status', 'legal_form', 'clinics_count',
  'convention_scope', 'selarl_is_manager', 'urssaf_registered', 'carcdsf_registered', 'acre_status',
  'contribution_relief_status', 'cross_border_activity_2026',
];

function errorText(error: unknown): string {
  const candidate = error as { response?: { data?: { detail?: unknown } } };
  return typeof candidate?.response?.data?.detail === 'string'
    ? candidate.response.data.detail : 'No se pudieron guardar los datos.';
}

function fromSettings(value: PVProfessionalSettings): ProfessionalDraft {
  return {
    activity_status: value.activity_status,
    activity_name: value.activity_name,
    activity_start_date: value.activity_start_date,
    activity_year_number: value.activity_year_number,
    vat_treatment: value.vat_treatment,
    france_convention_status: value.france_convention_status,
  };
}

function statusValue(value: boolean | null): string {
  return value === null ? '' : value ? 'yes' : 'no';
}
function parseStatus(value: string): boolean | null {
  return value === '' ? null : value === 'yes';
}

function FieldConfirmation({
  field, checked, valuePresent, onChange,
}: {
  field: PVProfileField;
  checked: boolean;
  valuePresent: boolean;
  onChange: (field: PVProfileField, checked: boolean) => void;
}) {
  return (
    <FormControlLabel
      sx={{ m: 0, alignSelf: 'flex-start', '& .MuiFormControlLabel-label': { fontSize: '.71rem', color: checked ? '#86efac' : '#94a3b8' } }}
      control={<Checkbox size="small" sx={{ p: .5, color: '#64748b', '&.Mui-checked': { color: '#86efac' } }}
        disabled={!valuePresent} checked={checked && valuePresent}
        onChange={(_, next) => onChange(field, next)} />}
      label={checked && valuePresent ? 'Confirmado' : 'Confirmar dato'}
    />
  );
}

export default function ProjectVitalDataView() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const [tab, setTab] = useState<Tab>('personal');
  const [profile, setProfile] = useState<PVProfileWrite>(EMPTY_PROFILE);
  const [professional, setProfessional] = useState<ProfessionalDraft>(EMPTY_PROFESSIONAL);
  const [confirmed, setConfirmed] = useState<PVProfileField[]>([]);
  const [formError, setFormError] = useState('');

  const profileQuery = useQuery({ queryKey: ['project-vital', 'profile'], queryFn: getPVProfile });
  const settingsQuery = useQuery({ queryKey: ['project-vital', 'professional-settings'], queryFn: getPVProfessionalSettings });

  useEffect(() => {
    if (!profileQuery.data || !settingsQuery.data) return;
    const { user_id: _id, created_at: _created, updated_at: _updated, ...fields } = profileQuery.data;
    void _id; void _created; void _updated;
    setProfile(fields);
    setProfessional(fromSettings(settingsQuery.data));
    setConfirmed(fields.confirmed_fields || []);
    setFormError('');
  }, [profileQuery.data, settingsQuery.data]);

  const isPresent = (field: PVProfileField): boolean => {
    if (field === 'household_status' || field === 'dependent_children') return Boolean(settingsQuery.data);
    if (field === 'activity_name' || field === 'activity_start_date' || field === 'activity_year_number'
      || field === 'vat_treatment' || field === 'france_convention_status') {
      const value = professional[field];
      return value !== null && value !== '' && value !== 'unknown';
    }
    const value = profile[field as keyof PVProfileWrite];
    return value !== null && value !== undefined && value !== '';
  };
  const setValue = <K extends keyof PVProfileWrite>(key: K, value: PVProfileWrite[K]) => {
    setProfile((before) => ({ ...before, [key]: value }));
    setConfirmed((before) => before.filter((item) => item !== key));
  };
  const setProfessionalValue = <K extends keyof ProfessionalDraft>(key: K, value: ProfessionalDraft[K]) => {
    setProfessional((before) => ({ ...before, [key]: value }));
    setConfirmed((before) => before.filter((item) => item !== key));
  };
  const setConfirmation = (field: PVProfileField, checked: boolean) => {
    setConfirmed((before) => checked ? [...new Set([...before, field])] : before.filter((item) => item !== field));
  };
  const check = (field: PVProfileField) => (
    <FieldConfirmation field={field} checked={confirmed.includes(field)}
      valuePresent={isPresent(field)} onChange={setConfirmation} />
  );

  const saveMutation = useMutation({
    mutationFn: async () => {
      // Fresh settings prevent this profile form from overwriting later finance edits.
      const settings = await getPVProfessionalSettings();
      const { user_id: _uid, created_at: _created, updated_at: _updated, ...settingsWrite } = settings;
      void _uid; void _created; void _updated;
      const changes = {
        ...settingsWrite,
        ...professional,
        activity_name: professional.activity_name?.trim() || null,
      } satisfies PVProfessionalSettingsWrite;
      if (JSON.stringify(fromSettings(settings)) !== JSON.stringify(fromSettings({ ...settings, ...changes }))) {
        await updatePVProfessionalSettings(changes);
      }
      const payload: PVProfileWrite = {
        ...profile,
        first_name: profile.first_name?.trim() || null,
        last_name: profile.last_name?.trim() || null,
        confirmed_fields: confirmed.filter(isPresent),
      };
      try {
        return await updatePVProfile(payload);
      } catch (error) {
        throw new Error(`No se pudo guardar la ficha complementaria. Si cambiaste datos profesionales, podrían haberse guardado: ${errorText(error)}`);
      }
    },
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['project-vital', 'profile'] }),
        queryClient.invalidateQueries({ queryKey: ['project-vital', 'professional-settings'] }),
        queryClient.invalidateQueries({ queryKey: ['project-vital', 'professional-monthly-control'] }),
      ]);
      setFormError('');
      enqueueSnackbar('Perfil guardado.', { variant: 'success' });
    },
    onError: (error) => setFormError(error instanceof Error ? error.message : errorText(error)),
  });

  const missingCount = useMemo(() => [...PERSONAL_FIELDS, ...PROFESSIONAL_FIELDS]
    .filter((field) => field !== 'selarl_is_manager' || profile.legal_form === 'selarl_associate')
    .filter((field) => !confirmed.includes(field)).length, [confirmed, profile.legal_form]);
  const block = { borderRadius: 3, bgcolor: '#111c2f', border: `1px solid ${alpha('#94a3b8', .10)}`, p: { xs: 1.5, md: 2.2 } };
  const grid = { display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' }, gap: 1.6 };
  const fieldBox = { minWidth: 0, display: 'flex', flexDirection: 'column' as const, gap: .25 };
  const textSelect = (value: string | number | null) => value ?? '';

  return (
    <Stack spacing={1.5}>
      <Box>
        <Typography component="h1" sx={{ fontWeight: 900, fontSize: { xs: '1.4rem', md: '1.9rem' } }}>Mis datos</Typography>
        <Typography sx={{ color: '#94a3b8', mt: .3, fontSize: '.8rem' }}>
          Completa tu información sin repetir lo que ya está guardado en Finanzas.
        </Typography>
      </Box>
      <Stack direction="row" spacing={.65} sx={{ overflowX: 'auto', pb: .3 }}>
        {([
          ['personal', 'Personal', <PersonOutlineRoundedIcon key="personal" />],
          ['professional', 'Actividad', <WorkOutlineRoundedIcon key="professional" />],
          ['integrations', 'Integraciones', <HubRoundedIcon key="integrations" />],
        ] as const).map(([value, label, icon]) => (
          <Button key={value} onClick={() => setTab(value)} startIcon={icon} size="small"
            sx={{ whiteSpace: 'nowrap', flexShrink: 0, textTransform: 'none', fontWeight: 800,
              color: tab === value ? '#061523' : '#94a3b8', bgcolor: tab === value ? CYAN : alpha('#fff', .035),
              '&:hover': { bgcolor: tab === value ? '#88ffff' : alpha('#fff', .06) } }}>
            {label}
          </Button>
        ))}
      </Stack>

      {tab === 'integrations' ? (
        <Box sx={grid}>
          <Paper sx={block} elevation={0}>
            <Typography sx={{ fontWeight: 800 }}>Google Calendar</Typography>
            <Typography sx={{ fontSize: '.8rem', color: '#94a3b8', mt: .5 }}>No conectado. Integración prevista inicialmente en modo lectura.</Typography>
          </Paper>
          <Paper sx={block} elevation={0}>
            <Typography sx={{ fontWeight: 800 }}>Estado Maestro</Typography>
            <Typography sx={{ fontSize: '.8rem', color: '#94a3b8', mt: .5 }}>Pendiente de integración como fuente de contexto.</Typography>
          </Paper>
        </Box>
      ) : profileQuery.isLoading || settingsQuery.isLoading ? (
        <Box sx={{ py: 6, textAlign: 'center' }}><CircularProgress size={26} /></Box>
      ) : profileQuery.isError || settingsQuery.isError ? (
        <Alert severity="error" action={<Button onClick={() => { void profileQuery.refetch(); void settingsQuery.refetch(); }}>Reintentar</Button>}>
          No se pudo cargar el perfil. Comprueba que la migración de perfiles esté aplicada.
        </Alert>
      ) : (
        <>
          <Paper elevation={0} sx={{ ...block, bgcolor: '#101d31' }}>
            <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={.6}>
              <Box>
                <Typography sx={{ fontWeight: 850, color: '#f8fafc' }}>Ficha personal y fiscal</Typography>
                <Typography sx={{ mt: .3, fontSize: '.76rem', color: '#94a3b8' }}>
                  Marca «Confirmar dato» solo cuando lo hayas comprobado. Los valores precargados no se consideran confirmados por defecto.
                </Typography>
              </Box>
              <Typography sx={{ fontSize: '.73rem', color: missingCount > 0 ? '#fcd34d' : '#86efac', fontWeight: 800, whiteSpace: 'nowrap' }}>
                {missingCount > 0 ? `${missingCount} datos por confirmar` : 'Datos confirmados'}
              </Typography>
            </Stack>
          </Paper>

          {tab === 'personal' ? (
            <Paper elevation={0} sx={block}>
              <Typography sx={{ fontWeight: 850, mb: 1.6 }}>Información personal</Typography>
              <Box sx={grid}>
                <Box sx={fieldBox}>
                  <TextField size="small" label="Nombre" value={profile.first_name ?? ''} onChange={(event) => setValue('first_name', event.target.value || null)} />
                  {check('first_name')}
                </Box>
                <Box sx={fieldBox}>
                  <TextField size="small" label="Apellidos" value={profile.last_name ?? ''} onChange={(event) => setValue('last_name', event.target.value || null)} />
                  {check('last_name')}
                </Box>
                <Box sx={fieldBox}>
                  <TextField size="small" select label="Residencia fiscal" value={textSelect(profile.tax_residence_country)}
                    onChange={(event) => setValue('tax_residence_country', (event.target.value || null) as PVProfileWrite['tax_residence_country'])}>
                    <MenuItem value="">Por confirmar</MenuItem><MenuItem value="FR">Francia</MenuItem><MenuItem value="ES">España</MenuItem><MenuItem value="other">Otro país</MenuItem>
                  </TextField>
                  <Typography sx={{ fontSize: '.7rem', color: '#64748b' }}>País en el que tributas como residente; no necesariamente el de tu trabajo.</Typography>
                  {check('tax_residence_country')}
                </Box>
                <Box sx={fieldBox}>
                  <TextField size="small" label="Residente fiscal desde (fecha exacta)" type="date" value={profile.tax_residence_since ?? ''}
                    InputLabelProps={{ shrink: true }} onChange={(event) => {
                      setValue('tax_residence_since', event.target.value || null);
                      if (event.target.value) setValue('tax_residence_since_month', null);
                    }} />
                  {check('tax_residence_since')}
                </Box>
                <Box sx={fieldBox}>
                  <TextField size="small" label="Mes aproximado de residencia (si no recuerdas el día)" type="month"
                    value={profile.tax_residence_since_month ?? ''}
                    InputLabelProps={{ shrink: true }}
                    onChange={(event) => {
                      setValue('tax_residence_since_month', event.target.value || null);
                      if (event.target.value) setValue('tax_residence_since', null);
                    }} />
                  <Typography sx={{ fontSize: '.7rem', color: '#94a3b8' }}>Es solo una referencia: no marca como confirmada la fecha exacta.</Typography>
                </Box>
                <Box sx={fieldBox}>
                  <TextField size="small" label="Situación familiar" value="Soltera (modelo Francia V1)" disabled />
                  <Typography sx={{ fontSize: '.7rem', color: '#64748b' }}>El motor francés actual solo calcula para una persona soltera. No se debe usar con otra situación hasta ampliar el modelo.</Typography>
                  {check('household_status')}
                </Box>
                <Box sx={fieldBox}>
                  <TextField size="small" label="Hijos/personas a cargo" value={settingsQuery.data?.dependent_children ?? 0} disabled />
                  {check('dependent_children')}
                </Box>
              </Box>
            </Paper>
          ) : (
            <>
              <Paper elevation={0} sx={block}>
                <Typography sx={{ fontWeight: 850, mb: 1.6 }}>Actividad profesional</Typography>
                <Box sx={grid}>
                  <Box sx={fieldBox}>
                    <TextField select size="small" label="Estado de actividad" value={professional.activity_status}
                      onChange={(event) => setProfessionalValue('activity_status', event.target.value as ProfessionalDraft['activity_status'])}>
                      <MenuItem value="pre_activity">Antes de empezar</MenuItem><MenuItem value="active">Activa</MenuItem><MenuItem value="paused">Pausada</MenuItem>
                    </TextField>
                  </Box>
                  <Box sx={fieldBox}>
                    <TextField size="small" label="Actividad" placeholder="P. ej. Odontología sanitaria" value={professional.activity_name ?? ''}
                      onChange={(event) => setProfessionalValue('activity_name', event.target.value || null)} />
                    {check('activity_name')}
                  </Box>
                  <Box sx={fieldBox}>
                    <TextField size="small" label="Fecha de inicio de actividad" type="date" value={professional.activity_start_date ?? ''}
                      InputLabelProps={{ shrink: true }} onChange={(event) => setProfessionalValue('activity_start_date', event.target.value || null)} />
                    {check('activity_start_date')}
                  </Box>
                  <Box sx={fieldBox}>
                    <TextField size="small" label="Año de ejercicio profesional" type="number" value={professional.activity_year_number}
                      inputProps={{ min: 1, max: 50 }} onChange={(event) => setProfessionalValue('activity_year_number', Number(event.target.value) || 1)} />
                    <Typography sx={{ fontSize: '.7rem', color: '#64748b' }}>1 si es tu primer año de actividad liberal.</Typography>
                    {check('activity_year_number')}
                  </Box>
                  <Box sx={fieldBox}>
                    <TextField select size="small" label="Forma de ejercicio" value={textSelect(profile.legal_form)}
                      onChange={(event) => {
                        const value = (event.target.value || null) as PVProfileWrite['legal_form'];
                        setValue('legal_form', value);
                        if (value !== 'selarl_associate') setValue('selarl_is_manager', null);
                      }}>
                      <MenuItem value="">Por confirmar</MenuItem><MenuItem value="individual">Persona física / individual</MenuItem>
                      <MenuItem value="company">Otra sociedad</MenuItem><MenuItem value="selarl_associate">Socia de SELARL</MenuItem><MenuItem value="other">Otra forma</MenuItem>
                    </TextField>
                    {check('legal_form')}
                  </Box>
                  {profile.legal_form === 'selarl_associate' && (
                    <Box sx={fieldBox}>
                      <TextField select size="small" label="¿Desempeñas funciones de gerencia en la SELARL?"
                        value={statusValue(profile.selarl_is_manager)}
                        onChange={(event) => setValue('selarl_is_manager', parseStatus(event.target.value))}>
                        <MenuItem value="">Por confirmar</MenuItem>
                        <MenuItem value="yes">Sí</MenuItem>
                        <MenuItem value="no">No, solo ejerzo como profesional</MenuItem>
                      </TextField>
                      {check('selarl_is_manager')}
                    </Box>
                  )}
                  <Box sx={fieldBox}>
                    <TextField size="small" type="number" label="Número de clínicas" inputProps={{ min: 1, max: 99 }} value={profile.clinics_count ?? ''}
                      onChange={(event) => setValue('clinics_count', event.target.value ? Number(event.target.value) : null)} />
                    {check('clinics_count')}
                  </Box>
                  <Box sx={fieldBox}>
                    <TextField select size="small" label="Actividad e IVA" value={professional.vat_treatment}
                      onChange={(event) => setProfessionalValue('vat_treatment', event.target.value as ProfessionalDraft['vat_treatment'])}>
                      <MenuItem value="exempt_healthcare">Odontología sanitaria exenta</MenuItem><MenuItem value="other">Otra / actividad mixta</MenuItem>
                    </TextField>
                    {check('vat_treatment')}
                  </Box>
                  <Box sx={fieldBox}>
                    <TextField select size="small" label="Convenio Assurance Maladie" value={professional.france_convention_status}
                      onChange={(event) => setProfessionalValue('france_convention_status', event.target.value as ProfessionalDraft['france_convention_status'])}>
                      <MenuItem value="unknown">Por confirmar</MenuItem><MenuItem value="conventionne">Conventionnée</MenuItem><MenuItem value="non_conventionne">No conventionnée</MenuItem>
                    </TextField>
                    {check('france_convention_status')}
                  </Box>
                  <Box sx={fieldBox}>
                    <TextField select size="small" label="Alcance del convenio" value={textSelect(profile.convention_scope)}
                      onChange={(event) => setValue('convention_scope', (event.target.value || null) as PVProfileWrite['convention_scope'])}>
                      <MenuItem value="">Por confirmar</MenuItem><MenuItem value="all">Toda la actividad</MenuItem>
                      <MenuItem value="partial">Solo parte de la actividad</MenuItem><MenuItem value="not_applicable">No aplica</MenuItem>
                    </TextField>
                    {check('convention_scope')}
                  </Box>
                </Box>
                {profile.legal_form === 'company' && (
                  <Alert severity="warning" sx={{ mt: 1.5 }}>La estimación fiscal BNC personal no sirve para calcular el resultado o los impuestos de una sociedad.</Alert>
                )}
                {profile.legal_form === 'selarl_associate' && (
                  <Alert severity="info" sx={{ mt: 1.5 }}>En Finanzas registra únicamente la remuneración profesional que te corresponde y recibes de la SELARL, con el reparto de la clínica ya aplicado. Nunca vuelvas a multiplicarla por un porcentaje ni registres la parte de la clínica como gasto personal. No es un cálculo del impuesto de sociedades. Las cotizaciones y los pagos que eventualmente asuma la SEL requieren conciliación con la documentación real.</Alert>
                )}
                {profile.convention_scope === 'partial' && (
                  <Alert severity="warning" sx={{ mt: 1.5 }}>El motor aún no distingue qué proporción de ingresos está acogida al convenio. La estimación social es provisional.</Alert>
                )}
              </Paper>
              <Paper elevation={0} sx={block}>
                <Typography sx={{ fontWeight: 850 }}>
                  Objetivo económico personal · {settingsQuery.data?.annual_revenue_target_year ?? 'ejercicio sin definir'}
                </Typography>
                <Typography sx={{ mt: .6, color: '#f8fafc', fontSize: '1.4rem', fontWeight: 900 }}>
                  {new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(Number(settingsQuery.data?.annual_revenue_target ?? 0))}
                </Typography>
                <Typography sx={{ color: '#94a3b8', fontSize: '.75rem', mt: .35 }}>
                  Solo remuneración recibida por la persona; nunca cifra de negocio total de la clínica.
                  La proyección dinámica se calcula a partir de los cobros registrados y de Planificación.
                </Typography>
                <Typography sx={{ color: '#94a3b8', fontSize: '.73rem', mt: .6 }}>
                  Base fiscal: {settingsQuery.data?.projection_method === 'adaptive' || settingsQuery.data?.projection_method === 'run_rate' ? 'proyección dinámica' : 'objetivo anual'}.
                </Typography>
                <Button size="small" component="a" href="/project-vital?view=finanzas" sx={{ mt: 1, textTransform: 'none', pl: 0 }}>
                  Revisar en Finanzas → Profesional → Fiscalidad
                </Button>
              </Paper>
              <Paper elevation={0} sx={block}>
                <Typography sx={{ fontWeight: 850, mb: 1.6 }}>Altas y situaciones por confirmar</Typography>
                <Box sx={grid}>
                  {([
                    ['urssaf_registered', 'Alta en URSSAF'],
                    ['carcdsf_registered', 'Afiliación CARCDSF'],
                    ['cross_border_activity_2026', 'Actividad o residencia fiscal en otro país durante 2026'],
                  ] as const).map(([key, label]) => (
                    <Box key={key} sx={fieldBox}>
                      <TextField select size="small" label={label} value={statusValue(profile[key])}
                        onChange={(event) => setValue(key, parseStatus(event.target.value))}>
                        <MenuItem value="">Por confirmar</MenuItem><MenuItem value="yes">Sí</MenuItem><MenuItem value="no">No</MenuItem>
                      </TextField>
                      {check(key)}
                    </Box>
                  ))}
                  <Box sx={fieldBox}>
                    <TextField select size="small" label="ACRE" value={textSelect(profile.acre_status)}
                      onChange={(event) => setValue('acre_status', (event.target.value || null) as PVProfileWrite['acre_status'])}>
                      <MenuItem value="">Por confirmar</MenuItem><MenuItem value="not_requested">No solicitada</MenuItem>
                      <MenuItem value="requested">Solicitada</MenuItem><MenuItem value="granted">Concedida</MenuItem><MenuItem value="denied">Denegada</MenuItem>
                    </TextField>
                    {check('acre_status')}
                  </Box>
                  <Box sx={fieldBox}>
                    <TextField select size="small" label="Dispensa/reducción de cotizaciones" value={textSelect(profile.contribution_relief_status)}
                      onChange={(event) => setValue('contribution_relief_status', (event.target.value || null) as PVProfileWrite['contribution_relief_status'])}>
                      <MenuItem value="">Por confirmar</MenuItem><MenuItem value="none">Ninguna</MenuItem>
                      <MenuItem value="requested">Solicitada</MenuItem><MenuItem value="granted">Concedida</MenuItem>
                    </TextField>
                    {check('contribution_relief_status')}
                  </Box>
                </Box>
              </Paper>
            </>
          )}

          {profile.tax_residence_country !== 'FR' || !confirmed.includes('tax_residence_country') ? (
            <Alert severity="info">La residencia fiscal francesa aún no está confirmada. No tomes la estimación de IR francés como definitiva hasta resolverlo.</Alert>
          ) : null}
          {formError && <Alert severity="error">{formError}</Alert>}
          <Button variant="contained" startIcon={<SaveRoundedIcon />} disabled={saveMutation.isPending}
            onClick={() => { setFormError(''); saveMutation.mutate(); }}
            sx={{ alignSelf: { xs: 'stretch', sm: 'flex-end' }, bgcolor: CYAN, color: '#07111f', fontWeight: 850, textTransform: 'none' }}>
            {saveMutation.isPending ? 'Guardando…' : 'Guardar ficha'}
          </Button>
          <Typography sx={{ color: '#64748b', fontSize: '.72rem' }}>
            Los datos de este perfil pertenecen únicamente al usuario con la sesión abierta. No se copian entre cuentas.
          </Typography>
        </>
      )}
    </Stack>
  );
}
